// src/modules/Shop/components/CartDrawer.js
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaTimes, FaPlus, FaMinus, FaTrash, FaShoppingBag } from 'react-icons/fa';
import { useCart } from '../contexts/CartContext';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 200;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 300ms;
`;

const Drawer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 420px;
  max-width: 100%;
  height: 100vh;
  background: white;
  z-index: 201;
  display: flex;
  flex-direction: column;
  transform: translateX(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 300ms ease;
`;

const DrawerHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: space-between;

  h2 {
    font-size: 18px;
    font-weight: 600;
    color: #111;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: #666;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #111;
  }
`;

const DrawerContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 48px 24px;

  svg {
    font-size: 48px;
    color: #ddd;
    margin-bottom: 16px;
  }

  h3 {
    font-size: 18px;
    color: #333;
    margin-bottom: 8px;
  }

  p {
    color: #666;
    font-size: 14px;
    margin-bottom: 24px;
  }
`;

const ContinueShoppingButton = styled(Link)`
  display: inline-block;
  background: #111;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
  transition: opacity 200ms;

  &:hover {
    opacity: 0.9;
  }
`;

const CartItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CartItem = styled.div`
  display: flex;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const ItemImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  background: #f5f5f5;
`;

const ItemDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled(Link)`
  font-size: 14px;
  font-weight: 500;
  color: #111;
  text-decoration: none;
  display: block;
  margin-bottom: 4px;

  &:hover {
    color: #00D4AA;
  }
`;

const ItemSku = styled.span`
  font-size: 12px;
  color: #999;
  display: block;
  margin-bottom: 8px;
`;

const ItemPrice = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const QuantityButton = styled.button`
  width: 28px;
  height: 28px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    border-color: #111;
    background: #f5f5f5;
  }

  svg {
    font-size: 10px;
  }
`;

const QuantityValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  min-width: 24px;
  text-align: center;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
  margin-left: auto;

  &:hover {
    color: #ef4444;
  }
`;

const DrawerFooter = styled.div`
  padding: 24px;
  border-top: 1px solid #eee;
  background: #fafafa;
`;

const Subtotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  span {
    font-size: 14px;
    color: #666;
  }

  strong {
    font-size: 20px;
    color: #111;
  }
`;

const CheckoutButton = styled(Link)`
  display: block;
  width: 100%;
  background: #00D4AA;
  color: #000;
  text-align: center;
  padding: 16px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 15px;
  transition: opacity 200ms;

  &:hover {
    opacity: 0.9;
  }
`;

const ViewCartLink = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 12px;
  color: #666;
  font-size: 13px;
  text-decoration: none;

  &:hover {
    color: #111;
    text-decoration: underline;
  }
`;

const formatPrice = (price) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(price);
};

const CartDrawer = () => {
  const {
    items,
    itemCount,
    subtotal,
    isOpen,
    closeCart,
    updateQuantity,
    removeItem
  } = useCart();

  return (
    <>
      <Overlay $isOpen={isOpen} onClick={closeCart} />
      <Drawer $isOpen={isOpen}>
        <DrawerHeader>
          <h2>
            <FaShoppingBag />
            Carrinho ({itemCount})
          </h2>
          <CloseButton onClick={closeCart}>
            <FaTimes />
          </CloseButton>
        </DrawerHeader>

        <DrawerContent>
          {items.length === 0 ? (
            <EmptyCart>
              <FaShoppingBag />
              <h3>Seu carrinho esta vazio</h3>
              <p>Adicione produtos para continuar comprando</p>
              <ContinueShoppingButton to="/" onClick={closeCart}>
                Ver produtos
              </ContinueShoppingButton>
            </EmptyCart>
          ) : (
            <CartItems>
              {items.map(item => (
                <CartItem key={item.product_id}>
                  <ItemImage
                    src={item.image || 'https://placehold.co/80x80/f5f5f5/999?text=Produto'}
                    alt={item.name}
                  />
                  <ItemDetails>
                    <ItemName to={`/produto/${item.slug}`} onClick={closeCart}>
                      {item.name}
                    </ItemName>
                    {item.sku && <ItemSku>SKU: {item.sku}</ItemSku>}
                    <ItemPrice>{formatPrice(item.price)}</ItemPrice>

                    <QuantityControls>
                      <QuantityButton onClick={() => updateQuantity(item.product_id, item.quantity - 1)}>
                        <FaMinus />
                      </QuantityButton>
                      <QuantityValue>{item.quantity}</QuantityValue>
                      <QuantityButton onClick={() => updateQuantity(item.product_id, item.quantity + 1)}>
                        <FaPlus />
                      </QuantityButton>
                    </QuantityControls>
                  </ItemDetails>

                  <RemoveButton onClick={() => removeItem(item.product_id)} title="Remover">
                    <FaTrash />
                  </RemoveButton>
                </CartItem>
              ))}
            </CartItems>
          )}
        </DrawerContent>

        {items.length > 0 && (
          <DrawerFooter>
            <Subtotal>
              <span>Subtotal</span>
              <strong>{formatPrice(subtotal)}</strong>
            </Subtotal>
            <CheckoutButton to="/checkout" onClick={closeCart}>
              Finalizar Compra
            </CheckoutButton>
            <ViewCartLink to="/carrinho" onClick={closeCart}>
              Ver carrinho completo
            </ViewCartLink>
          </DrawerFooter>
        )}
      </Drawer>
    </>
  );
};

export default CartDrawer;
